/**
 * Pipeline worker controls - run discover/classify/contacts/ICP/dedup from UI.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, GitMerge, Target, Briefcase, Users, Tags, Sparkles, Linkedin,
} from 'lucide-react';

import type { ApiCall } from '../../api/interview';
import {
  triggerDiscoverJobs, triggerClassifyJobs, triggerDiscoverContacts,
  triggerDiscoverDecisionMakers,
  triggerScoreIcp, previewDuplicateCandidates, mergeDuplicateCompanies,
  listDiscoveryRuns, type DiscoveryRun, type DuplicateCandidatesResult,
} from '../../api/b2b';
import { HeadingWithInfo, LoadingNudge, ActionWithInfo, InfoTip } from './_ui';

interface Props {
  apiCall: ApiCall;
  onCompaniesChanged: () => void;
}

type BusyKey =
  | 'discoverJobs' | 'classifyJobs' | 'discoverContacts' | 'discoverLeaders'
  | 'scoreIcp' | 'mergeScan' | 'mergeRun';

const WORKER_INFOS = {
  panel:
    'Batch-Schritte für Firmen die schon in der Liste sind. '
    + 'Einen Worker nach dem anderen starten (~15-30 Min). '
    + 'Reihenfolge: Discover jobs → Classify → Find leaders → Find contacts. '
    + 'Optionen unten steuern Skip vs. Force-Refresh.',
    discoverJobs:
    'Probiert ATS (Personio, Ashby, Greenhouse, Workday…) und holt offene Stellen. '
    + 'Limit + Tier-Filter unten steuerbar (200-2000). '
    + 'Standard: priorisiert ohne Jobs; Optionen skippen Firmen mit aktiven Jobs oder ATS.',
  classifyJobs:
    'LLM sortiert Jobs (Backend, DevOps…) und setzt Firmen-Tier hot/warm/cold '
    + 'nach ICP-Signalen. Standard: nur noch nicht klassifizierte Jobs.',
  discoverContacts:
    'Scraped Impressum, Team- und Karriere-Seiten nach Email-Adressen. '
    + 'Standard: nur Firmen mit Jobs und <3 Contacts.',
  discoverLeaders:
    'Sucht CTO/Founder via Team-Seite, Impressum und Web-Suche. '
    + 'Legt den besten Kandidaten als Contact an (max 50). '
    + 'Standard: überspringt Firmen mit Leader-Contact.',
  scoreIcp:
    'Berechnet hot/warm/cold neu aus Job-Signalen - ohne Jobs neu zu klassifizieren.',
  scanDupes:
    'Findet gleichen Firmennamen mit verschiedenen Domains (z.B. enpal.de + enpal.com).',
  mergeDupes:
    'Führt Duplikat-Firmen zusammen - Jobs und Contacts wandern zur Haupt-Domain.',
} as const;

const B2BWorkersPanel: React.FC<Props> = ({ apiCall, onCompaniesChanged }) => {
  const [busy, setBusy] = useState<BusyKey | null>(null);
  const [busyDetail, setBusyDetail] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dupPreview, setDupPreview] = useState<DuplicateCandidatesResult | null>(null);
  const [runs, setRuns] = useState<DiscoveryRun[]>([]);

  const [onlyWithoutActiveJobs, setOnlyWithoutActiveJobs] = useState(false);
  const [onlyWithoutAts, setOnlyWithoutAts] = useState(false);
  const [discoverJobsLimit, setDiscoverJobsLimit] = useState(200);
  const [discoverJobsTiers, setDiscoverJobsTiers] = useState('');
  const [recategorize, setRecategorize] = useState(false);
  const [contactsAll, setContactsAll] = useState(false);
  const [contactsForce, setContactsForce] = useState(false);
  const [leadersAll, setLeadersAll] = useState(false);
  const [leadersForce, setLeadersForce] = useState(false);

  const loadRuns = useCallback(async () => {
    try {
      setRuns(await listDiscoveryRuns(apiCall));
    } catch { /* ignore */ }
  }, [apiCall]);

  useEffect(() => { loadRuns(); }, [loadRuns]);
  useEffect(() => {
    const active = runs.some((r) =>
      ['discover_jobs', 'classify_jobs', 'discover_contacts', 'discover_decision_makers', 'score_icp', 'generate_drafts', 'send_batch', 'poll_replies']
        .includes(r.kind)
      && (r.status === 'queued' || r.status === 'running'),
    );
    if (!active) return;
    const id = setInterval(loadRuns, 4000);
    return () => clearInterval(id);
  }, [runs, loadRuns]);

  const doneWorkerRuns = runs.filter((r) =>
    ['discover_jobs', 'classify_jobs', 'discover_contacts', 'discover_decision_makers', 'score_icp'].includes(r.kind)
    && r.status === 'done',
  ).map((r) => r.id).join(',');

  useEffect(() => {
    if (doneWorkerRuns) {
      onCompaniesChanged();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneWorkerRuns]);

  const runWorker = async (
    key: BusyKey,
    detail: string,
    fn: () => Promise<{ run_id: string }>,
    okMsg: string,
  ) => {
    setBusy(key);
    setBusyDetail(detail);
    setError(null);
    setFeedback(null);
    try {
      const r = await fn();
      setFeedback(`${okMsg} (run ${r.run_id.slice(0, 8)}…)`);
      await loadRuns();
    } catch (e: any) {
      setError(e?.message || 'Worker failed');
    } finally {
      setBusy(null);
      setBusyDetail(null);
    }
  };

  const scanDupes = async () => {
    setBusy('mergeScan');
    setBusyDetail('Scanning for duplicate company names across domains…');
    setError(null);
    try {
      const r = await previewDuplicateCandidates(apiCall);
      setDupPreview(r);
      setFeedback(`${r.total_groups} duplicate groups (${r.total_merge_candidates} rows to merge)`);
    } catch (e: any) {
      setError(e?.message || 'Scan failed');
    } finally {
      setBusy(null);
      setBusyDetail(null);
    }
  };

  const mergeDupes = async () => {
    if (!dupPreview?.total_merge_candidates) return;
    setBusy('mergeRun');
    setBusyDetail(`Merging ${dupPreview.total_merge_candidates} duplicate companies…`);
    setError(null);
    try {
      const r = await mergeDuplicateCompanies(apiCall, false);
      setDupPreview(null);
      setFeedback(`Merged ${r.companies_merged ?? 0} duplicate companies`);
      onCompaniesChanged();
    } catch (e: any) {
      setError(e?.message || 'Merge failed');
    } finally {
      setBusy(null);
      setBusyDetail(null);
    }
  };

  const disabled = busy !== null;

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-2 space-y-2">
      <div className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5 text-pink-600" />
        <HeadingWithInfo info={WORKER_INFOS.panel}>Pipeline workers</HeadingWithInfo>
      </div>
      <div className="flex flex-wrap gap-1.5">
        <ActionWithInfo info={WORKER_INFOS.discoverJobs}>
          <WorkerBtn
            icon={Briefcase}
            label="Discover jobs"
            busy={busy === 'discoverJobs'}
            disabled={disabled}
            onClick={() => runWorker(
              'discoverJobs',
              onlyWithoutActiveJobs
                ? 'ATS detect/fetch - only companies without active jobs…'
                : onlyWithoutAts
                  ? 'ATS detect/fetch - only companies without ATS…'
                  : `Polling ATS APIs (up to ${discoverJobsLimit})…`,
              () => triggerDiscoverJobs(apiCall, {
                limit: discoverJobsLimit,
                only_without_active_jobs: onlyWithoutActiveJobs,
                only_without_ats: onlyWithoutAts,
                tiers: discoverJobsTiers || undefined,
              }),
              'Discover jobs started',
            )}
          />
        </ActionWithInfo>
        <ActionWithInfo info={WORKER_INFOS.classifyJobs}>
          <WorkerBtn
            icon={Tags}
            label="Classify + ICP"
            busy={busy === 'classifyJobs'}
            disabled={disabled}
            onClick={() => runWorker(
              'classifyJobs',
              recategorize
                ? 'Re-classifying ALL active jobs + ICP…'
                : 'LLM-classifying new jobs + updating company tiers…',
              () => triggerClassifyJobs(apiCall, {
                limit: 200,
                score_icp_after: true,
                recategorize,
              }),
              'Classify jobs started',
            )}
          />
        </ActionWithInfo>
        <ActionWithInfo info={WORKER_INFOS.discoverContacts}>
          <WorkerBtn
            icon={Users}
            label="Find contacts"
            busy={busy === 'discoverContacts'}
            disabled={disabled}
            onClick={() => runWorker(
              'discoverContacts',
              'Scraping Impressum/careers for emails…',
              () => triggerDiscoverContacts(apiCall, {
                limit: 100,
                all: contactsAll,
                force: contactsForce,
              }),
              'Discover contacts started',
            )}
          />
        </ActionWithInfo>
        <ActionWithInfo info={WORKER_INFOS.discoverLeaders}>
          <WorkerBtn
            icon={Linkedin}
            label="Find leaders"
            busy={busy === 'discoverLeaders'}
            disabled={disabled}
            onClick={() => runWorker(
              'discoverLeaders',
              'Auto-search CTO/founders + add top contact (up to 50)…',
              () => triggerDiscoverDecisionMakers(apiCall, {
                limit: 50,
                all: leadersAll,
                force: leadersForce,
              }),
              'Find leaders started',
            )}
          />
        </ActionWithInfo>
        <ActionWithInfo info={WORKER_INFOS.scoreIcp}>
          <WorkerBtn
            icon={Target}
            label="Score ICP only"
            busy={busy === 'scoreIcp'}
            disabled={disabled}
            onClick={() => runWorker(
              'scoreIcp',
              'Recomputing cold/warm/hot tiers from job signals…',
              () => triggerScoreIcp(apiCall),
              'ICP scoring started',
            )}
          />
        </ActionWithInfo>
        <ActionWithInfo info={WORKER_INFOS.scanDupes}>
          <WorkerBtn
            icon={GitMerge}
            label="Scan dupes"
            busy={busy === 'mergeScan'}
            disabled={disabled}
            onClick={scanDupes}
          />
        </ActionWithInfo>
        {dupPreview && (dupPreview.total_merge_candidates ?? 0) > 0 && (
          <ActionWithInfo info={WORKER_INFOS.mergeDupes}>
            <WorkerBtn
              icon={GitMerge}
              label={`Merge ${dupPreview.total_merge_candidates}`}
              busy={busy === 'mergeRun'}
              disabled={disabled}
              accent
              onClick={mergeDupes}
            />
          </ActionWithInfo>
        )}
      </div>

      <div className="rounded border border-slate-200 bg-white px-2 py-1.5 space-y-1">
        <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1">
          Worker-Optionen
          <InfoTip
            align="left"
            text="Standard = skippt Erledigtes. Checkboxen = gezielt alles nochmal oder erweiterte Auswahl."
          />
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-600">
          <label className="inline-flex items-center gap-1.5">
            <span>Discover limit</span>
            <select
              value={discoverJobsLimit}
              onChange={(e) => setDiscoverJobsLimit(Number(e.target.value))}
              className="text-[11px] border border-slate-200 rounded px-1 py-0.5"
            >
              <option value={200}>200</option>
              <option value={500}>500</option>
              <option value={2000}>2000</option>
            </select>
          </label>
          <label className="inline-flex items-center gap-1.5">
            <span>Discover tiers</span>
            <select
              value={discoverJobsTiers}
              onChange={(e) => setDiscoverJobsTiers(e.target.value)}
              className="text-[11px] border border-slate-200 rounded px-1 py-0.5 max-w-[140px]"
              title="Welche Firmen-Tiers der Worker berücksichtigt"
            >
              <option value="">Standard (skip blocked/lost)</option>
              <option value="cold">Nur cold</option>
              <option value="cold,warm">cold + warm</option>
              <option value="cold,warm,hot">cold + warm + hot</option>
              <option value="customer">Nur customer</option>
            </select>
          </label>
          <WorkerOption
            checked={onlyWithoutActiveJobs}
            onChange={setOnlyWithoutActiveJobs}
            label="Discover jobs: nur ohne aktive Jobs"
            info="Kein Refresh bei Firmen die schon Stellen haben - spart API-Calls."
          />
          <WorkerOption
            checked={onlyWithoutAts}
            onChange={setOnlyWithoutAts}
            label="Discover jobs: nur ohne ATS"
            info="Nur Firmen ohne erkanntes ATS - gut für Erst-Detection."
          />
          <WorkerOption
            checked={recategorize}
            onChange={setRecategorize}
            label="Classify: bereits klassifizierte nochmal"
            info="LLM läuft erneut über alle aktiven Jobs (teurer, ~7 min/200 Jobs)."
          />
          <WorkerOption
            checked={contactsAll}
            onChange={setContactsAll}
            label="Contacts: auch ohne Jobs"
            info="Standard: nur Firmen mit offenen Stellen."
          />
          <WorkerOption
            checked={contactsForce}
            onChange={setContactsForce}
            label="Contacts: auch mit ≥3 Contacts"
            info="Standard: stoppt wenn genug Emails da sind."
          />
          <WorkerOption
            checked={leadersAll}
            onChange={setLeadersAll}
            label="Leaders: auch ohne Jobs"
            info="Standard: nur Firmen mit offenen Stellen."
          />
          <WorkerOption
            checked={leadersForce}
            onChange={setLeadersForce}
            label="Leaders: auch wenn Leader existiert"
            info="Standard: überspringt Firmen mit CTO/Founder-Contact."
          />
        </div>
      </div>

      {dupPreview && (dupPreview.groups?.length ?? 0) > 0 && (
        <div className="text-[11px] text-slate-600 max-h-24 overflow-y-auto border border-slate-200 rounded bg-white p-1.5">
          {dupPreview.groups!.slice(0, 8).map((g, i) => (
            <div key={i} className="truncate">
              {g.display_name}: {g.domains.join(' · ')}
            </div>
          ))}
          {(dupPreview.total_groups ?? 0) > 8 && (
            <div>…and {(dupPreview.total_groups ?? 0) - 8} more groups</div>
          )}
        </div>
      )}

      {busy && busyDetail && (
        <LoadingNudge title="Running pipeline worker…" step={busyDetail} />
      )}
      {feedback && (
        <div className="rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-2 py-1">{feedback}</div>
      )}
      {error && (
        <div className="rounded bg-red-50 border border-red-200 text-red-800 text-xs px-2 py-1">{error}</div>
      )}
    </div>
  );
};

const WorkerOption: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  info: string;
}> = ({ checked, onChange, label, info }) => (
  <label className="inline-flex items-center gap-1 cursor-pointer select-none">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="rounded border-slate-300"
    />
    {label}
    <InfoTip text={info} align="left" />
  </label>
);

const WorkerBtn: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  busy?: boolean;
  disabled?: boolean;
  accent?: boolean;
}> = ({ icon: Icon, label, onClick, busy, disabled, accent }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs disabled:opacity-50 ${
      accent
        ? 'bg-pink-600 text-white hover:bg-pink-700'
        : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
    }`}
  >
    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
    {label}
  </button>
);

export default B2BWorkersPanel;
